'use client'

/* ---------------------------------
Gallery
--------------------------------- */

import React, { ReactElement } from 'react'
import Image from 'next/image'
import dynamic from 'next/dynamic'
import { Section, Title } from '@dynamo-caffeina-dev/airc-ui-kit'
import DOMPurify from 'isomorphic-dompurify'
import { SectionBackground } from '@/types'
import { GalleryModel } from '@/models/Gallery'
import {
  ColumnsPhotoAlbum,
  RenderImageContext,
  RenderImageProps,
} from 'react-photo-album'
import 'react-photo-album/columns.css'
import Spinner from '@/components/atoms/Spinner'

const Lightbox = dynamic(() => import('@/components/molecules/Lightbox'), {
  ssr: false,
  loading: () => (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000,
      }}
    >
      <Spinner
        size='large'
        color='var(--airc-ui__colors-accent-500)'
      />
    </div>
  ),
})

type OwnProps = {
  props: ReturnType<typeof GalleryModel>['props']
  modelledContent: ReturnType<typeof GalleryModel>['modelledContent']
}

export default function Gallery({
  props,
  modelledContent: extractedContent,
}: OwnProps): ReactElement {
  const [currentPhoto, setCurrentPhoto] = React.useState(-1)
  const { title: _title, images } = extractedContent ?? {}
  const { content: titleContent, ...title } = _title
  const { bg } = props ?? {}
  const isLightboxOpen = currentPhoto >= 0

  return (
    <Section
      // `bg` is not provided by the CMS in this component,
      // but it should be; so I'm leaving this here for now.
      bg={bg as SectionBackground | undefined}
      className={`section-gallery`}
    >
      <div className='section-gallery__wrapper'>
        {/* INTRO */}
        <div className={`section-gallery__title-wrapper `}>
          {titleContent ? (
            <Title
              className={`section-gallery__title`}
              {...title}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(titleContent),
              }}
            />
          ) : null}
        </div>

        {/* GALLERY */}
        <div className='section-gallery__gallery'>
          <ColumnsPhotoAlbum
            columns={(containerWidth) => {
              if (containerWidth < 400) return 2
              if (containerWidth < 800) return 3
              if (containerWidth < 960) return 4
              return 5
            }}
            render={{ image: renderNextImage }}
            onClick={({ index }) => setCurrentPhoto(index)}
            photos={images.photoGrid}
          />

          {/*
            Don't render until open,
            otherwise dynamic loading would be pointless
          */}
          {isLightboxOpen ? (
            <Lightbox
              className='section-gallery__lightbox'
              index={currentPhoto}
              open={isLightboxOpen}
              close={() => setCurrentPhoto(-1)}
              slides={images.lightbox}
            />
          ) : null}
        </div>
      </div>
    </Section>
  )
}

// https://react-photo-album.com/examples/nextjs
function renderNextImage(
  { alt = '', title, sizes }: RenderImageProps,
  { photo, width, height }: RenderImageContext
) {
  return (
    <div
      style={{
        width: '100%',
        position: 'relative',
        aspectRatio: `${width} / ${height}`,
      }}
      className='section-gallery__image-container'
    >
      <Image
        fill
        src={photo}
        alt={alt}
        title={title}
        sizes={sizes}
        placeholder={'blurDataURL' in photo ? 'blur' : undefined}
        className='section-gallery__image'
      />
    </div>
  )
}
